import { ChainName } from '@ui/types/chain';

import { DISABLE_EVENT_LIST, POSTHOG_PROJECT_TOKEN } from './constants';
import { useIsFeatureFlagLoadedStore } from './hooks/useIsFeatureFlagLoaded';

export const customizedPostHogProps = [
  'chainId',
  'chainName',
  'walletAddress',
  'connectWallet',
] as const;

export type PostHogPropsType = (typeof customizedPostHogProps)[number];
export type PostHogPropsMap = Partial<Record<PostHogPropsType, string>>;
let posthog: typeof import('posthog-js')['default'];

export const getPosthog = async () => {
  if (typeof window === 'undefined') {
    return;
  }
  if (!POSTHOG_PROJECT_TOKEN) {
    return;
  }
  if (posthog) return posthog;
  const instance = (await import('posthog-js')).default;
  posthog = instance;
  instance.init(POSTHOG_PROJECT_TOKEN, {
    api_host: 'https://app.posthog.com',
    autocapture: {
      css_selector_allowlist: ['[ph-autocapture]'], // List of CSS selectors, e.g. <Button data-ph-autocapture>Click me!</Button>
    },
    debug: false,
    verbose: false,
    disable_session_recording: true,
    // Enable debug mode in development
    loaded: (posthog) => {
      posthog.debug(false);

      posthog.onFeatureFlags(() => {
        // Note: recording if needed, but we haven't planned what to record for now, but created a feature flag for this just in case
        // this is just an example, it's not a must to start recording in this `loaded` function
        // if (posthog.isFeatureEnabled('session_recording')) {
        //   posthog.startSessionRecording();
        // }
        useIsFeatureFlagLoadedStore.getState().setIsLoaded(true);
      });
    },
  });

  return posthog;
};
/**
 *  Super Props : every capture event will carry with the props if registered
 *
 *  register props only when really needed, and so that do not need to set common props
 *
 * @param registerProps
 */
export const registerPostHogProps = (registerProps: PostHogPropsMap) => {
  getPosthog().then((posthog) => {
    posthog?.register(registerProps);
  });
};

/**
 *  this is for unregister super props
 *
 * @param unregisterProps array of unregistered props name
 */
export const unRegisterPostHogProps = (unregisterProps: PostHogPropsType[]) => {
  getPosthog().then((posthog) => {
    unregisterProps.forEach((prop) => {
      posthog?.unregister(prop);
    });
  });
};

// unregister all of the default customized user props
export const unRegisterPostHogPropsDefault = () => {
  getPosthog().then((posthog) => {
    customizedPostHogProps.forEach((prop) => {
      posthog?.unregister(prop);
    });
  });
};

export const identifyUserInfo = ({
  address,
  chainName,
  chainId,
  connectWallet,
}: {
  address: string;
  chainName: ChainName;
  chainId: string | undefined;
  connectWallet?: string;
}) => {
  getPosthog().then((posthog) => {
    posthog?.identify(address, {
      chainId: chainId + '',
      chainName,
      walletAddress: address,
      connectWallet,
    });
  });
};

export const resetPostHog = () => {
  getPosthog().then((posthog) => {
    posthog?.reset();
  });
};

export const trackEvent = (eventName: string, props?: Record<string, any>) => {
  if (DISABLE_EVENT_LIST.includes(eventName)) return;

  getPosthog().then((posthog) => {
    posthog?.capture(eventName, {
      ...(props ?? {}),
      // chainName: getCurrentChainInfo()?.label,
    });
  });
};
